// src/pages/SignInPage.js
// Complete, line-by-line regeneration with the SSO “no new user creation” fix (v1.9.0):
// • No UI path to create accounts from Google SSO.
// • On SSO “User not found”: show Support dialog + “Link existing account” only.
// • Fixed Google fetch URLs (no /undefined/).
// • Single, robust apiBase derived from config baseUrl (fallback to origin).
// • Consistent setUser shape (object) across online/offline paths.
// • Offline-first guard for normal sign-in.
// • Gentle parsing of client/user perms.

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSpring, animated } from "react-spring";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faCheckCircle,
  faUser,
  faLock,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../assets/logo.jpg";
import { useUser } from "ogcommon";
import { baseUrl } from "./config";

/* ----------------------- helpers (module-level) ----------------------- */

// Prefer configured baseUrl; otherwise fall back to current origin.
// Trim trailing slashes for consistent concatenation.
const apiBase =
  typeof baseUrl === "string" && baseUrl.length
    ? baseUrl.replace(/\/+$/, "")
    : window.location.origin;

const parseJSONLoose = (p) => {
  if (p == null) return null;
  if (typeof p === "object") return p;
  if (typeof p === "string") {
    try {
      return JSON.parse(p);
    } catch {
      try {
        const cleaned = p
          .replace(/([{,]\s*)'([^']+?)'(\s*:)/g, '$1"$2"$3')
          .replace(/'true'|'false'/gi, (m) =>
            m.toLowerCase().replace(/'/g, "")
          );
        return JSON.parse(cleaned);
      } catch {
        return null;
      }
    }
  }
  return null;
};

const toBool = (v) => v === true || v === "true" || v === 1 || v === "1";

const hashPassword = (password) => {
  // Simple obfuscation for offline credential comparison (not cryptographic)
  let hash = 0,
    i,
    chr;
  for (i = 0; i < password.length; i++) {
    chr = password.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash.toString();
};

/* ----------------------------- component ------------------------------ */

function SignInPage() {
  const navigate = useNavigate();
  const { setUser } = useUser();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [subdomain, setSubdomain] = useState("");

  // SSO “user not found” + link-existing flow
  const [showNotFound, setShowNotFound] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [token, setToken] = useState("");
  const [linkUserID, setLinkUserID] = useState("");
  const [linkPassword, setLinkPassword] = useState("");
  const [linkError, setLinkError] = useState("");

  // Subtle entrance animation
  const formAnimation = useSpring({
    opacity: 1,
    transform: "translateY(0)",
    from: { opacity: 0, transform: "translateY(50px)" },
    config: { mass: 1, tension: 200, friction: 20 },
  });

  /* ------------------------- derive subdomain label ------------------------- */
  useEffect(() => {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    setSubdomain(parts.length > 2 ? parts[0] : "");
  }, []);

  /* ----------------------------- actions ------------------------------ */

  const handleForgotPassword = async () => {
    if (!username.trim()) {
      setError("Please enter a username or email first.");
      return;
    }
    try {
      const res = await fetch(`${apiBase}/api/passwordreset.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userID: username }),
      });
      const data = await res.json();
      if (data?.success) {
        setSuccessMessage(
          data?.message || "Password reset email sent successfully"
        );
        setError("");
      } else {
        setError(data?.message || "Failed to send password reset email");
        setSuccessMessage("");
      }
    } catch {
      setError("An error occurred while sending password reset email.");
      setSuccessMessage("");
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    // Offline fallback path
    if (!navigator.onLine) {
      handleOfflineSignIn();
      return;
    }

    try {
      const response = await fetch(`${apiBase}/api/login_api.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      const {
        success,
        message,
        user,
        companyName,
        perms: clientPerms,
      } = data || {};

      if (!success) {
        setError(message || "Login failed.");
        return;
      }

      // CLIENT-LEVEL flags from company perms
      let clientPermsObj = parseJSONLoose(clientPerms);
      if (
        !clientPermsObj ||
        typeof clientPermsObj !== "object" ||
        Array.isArray(clientPermsObj)
      ) {
        clientPermsObj = { hasOgFiles: false, UseFieldTicket: false };
      }
      const hasOgFiles = toBool(clientPermsObj.hasOgFiles);
      const canUseFieldTicket = toBool(clientPermsObj.UseFieldTicket);

      // USER-LEVEL perms (ACL object)
      const userPermsObj = parseJSONLoose(user?.perms) || {};

      // Push into context (consistent object shape)
      setUser({
        role: user?.Role,
        id: user?.UserID,
        company: companyName,
        jRole: user?.JobRole,
        perms: {
          ...userPermsObj,
          hasOgFiles,
          UseFieldTicket: canUseFieldTicket,
        },
      });

      // Persist essentials
      localStorage.setItem("userRole", user?.Role ?? "");
      localStorage.setItem("userID", user?.UserID ?? "");
      localStorage.setItem("companyName", companyName ?? "");
      localStorage.setItem("jobRole", user?.JobRole ?? "");
      localStorage.setItem("hasOgFiles", JSON.stringify(hasOgFiles));
      localStorage.setItem("UseFieldTicket", JSON.stringify(canUseFieldTicket));
      localStorage.setItem(
        "perms",
        JSON.stringify({ hasOgFiles, UseFieldTicket: canUseFieldTicket })
      );

      // Store offline credentials snapshot
      const credentialsPerms = {
        ...userPermsObj,
        hasOgFiles,
        UseFieldTicket: canUseFieldTicket,
      };
      localStorage.setItem(
        "credentials",
        JSON.stringify({
          username,
          password: hashPassword(password),
          role: user?.Role,
          userID: user?.UserID,
          companyName,
          perms: credentialsPerms,
          jobRole: user?.JobRole ?? "",
        })
      );

      // Role-aware redirect
      navigate(user?.Role === "A" ? "/ticketGrid" : "/home");
    } catch {
      setError("An error occurred while signing in.");
    }
  };

  const handleOfflineSignIn = () => {
    setError("");
    const stored = localStorage.getItem("credentials");
    if (!stored) {
      setError("No saved credentials available. Please sign in online first.");
      return;
    }

    try {
      const creds = JSON.parse(stored);
      const {
        username: storedUser,
        password: storedPassHash,
        role,
        userID: storedUserID,
        companyName,
        perms,
        jobRole,
      } = creds || {};
      const enteredHash = hashPassword(password);

      if (username === storedUser && enteredHash === storedPassHash) {
        // Set context from stored snapshot
        setUser({
          role,
          id: storedUserID,
          company: companyName,
          jRole: jobRole ?? "",
          perms: perms || {},
        });

        // Persist essentials again (freshen)
        localStorage.setItem("userRole", role ?? "");
        localStorage.setItem("userID", storedUserID ?? "");
        localStorage.setItem("companyName", companyName ?? "");
        if (perms) {
          if ("hasOgFiles" in perms)
            localStorage.setItem(
              "hasOgFiles",
              JSON.stringify(!!perms.hasOgFiles)
            );
          if ("UseFieldTicket" in perms)
            localStorage.setItem(
              "UseFieldTicket",
              JSON.stringify(!!perms.UseFieldTicket)
            );
          localStorage.setItem("perms", JSON.stringify(perms));
        }

        navigate(role === "A" ? "/ticketGrid" : "/home");
      } else {
        setError("Invalid credentials. Please sign in online first.");
      }
    } catch {
      setError("Offline credentials are invalid. Please sign in online first.");
    }
  };

  // Google OAuth: success
  const handleGoogleLoginSuccess = (resp) => {
    const googleToken = resp?.credential;
    if (!googleToken) {
      setError("Google token missing.");
      return;
    }

    fetch(`${apiBase}/api/google_login.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: googleToken }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success) {
          const { user, companyName } = data;

          // Note: server may not return user.perms as object → normalize
          const userPermsObj = parseJSONLoose(user?.perms) || {};

          setUser({
            role: user?.Role,
            id: user?.UserID,
            company: companyName,
            jRole: user?.JobRole,
            perms: userPermsObj,
          });

          localStorage.setItem("userRole", user?.Role ?? "");
          localStorage.setItem("userID", user?.UserID ?? "");
          localStorage.setItem("companyName", companyName ?? "");

          // Role-aware redirect: admin → ticketGrid; others → home
          navigate(user?.Role === "A" ? "/ticketGrid" : "/home");
          return;
        }

        if (data?.message === "User not found") {
          // Per policy: no auto-creation. Show Support dialog; allow linking.
          setToken(googleToken);
          setShowNotFound(true);
          return;
        }

        setError(data?.message || "Google Sign-In failed.");
      })
      .catch(() => {
        setError("An error occurred during Google Sign-In.");
      });
  };

  // Google OAuth: error
  const handleGoogleLoginError = () => {
    setError("Google Sign-In failed.");
  };

  // Link existing account to Google (no creation)
  const handleLinkExisting = async () => {
    setLinkError("");
    try {
      const res = await fetch(`${apiBase}/api/google_login.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          userID: linkUserID.trim(),
          password: linkPassword,
        }),
      });
      const data = await res.json();
      if (data?.success) {
        const { user, companyName } = data;
        const userPermsObj = parseJSONLoose(user?.perms) || {};
        setUser({
          role: user?.Role,
          id: user?.UserID,
          company: companyName,
          jRole: user?.JobRole,
          perms: userPermsObj,
        });

        localStorage.setItem("userRole", user?.Role ?? "");
        localStorage.setItem("userID", user?.UserID ?? "");
        localStorage.setItem("companyName", companyName ?? "");

        setShowLinkModal(false);
        navigate(user?.Role === "A" ? "/ticketGrid" : "/home");
      } else {
        setLinkError(data?.message || "Failed to link Google to your account.");
      }
    } catch {
      setLinkError("An error occurred while linking your account.");
    }
  };

  /* ------------------------------- render ------------------------------- */

  return (
    <GoogleOAuthProvider clientId="43210536118-10g29la6m8pfd5epk6u16851igpnlrqc.apps.googleusercontent.com">
      <div className="min-h-screen flex flex-col justify-center items-center relative overflow-hidden bg-gradient-to-r from-gray-100 to-gray-200">
        <animated.div
          style={formAnimation}
          className="bg-white shadow-2xl rounded-lg px-6 py-10 sm:px-12 sm:py-14 max-w-md w-full transform hover:scale-105 transition-transform duration-500 relative z-10 flex flex-col justify-center min-h-screen sm:min-h-0"
        >
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-gray-400 to-gray-500 rounded-t-lg animate-pulse"></div>

          <img
            src={logo}
            className="w-40 sm:w-32 mx-auto mb-6 sm:mb-8 rounded-full border-4 border-white shadow-md"
            alt="logo"
          />

          {subdomain && (
            <p className="text-xl font-semibold text-gray-800 text-center mb-6">
              {subdomain.charAt(0).toUpperCase() + subdomain.slice(1)}
            </p>
          )}

          <form className="space-y-6 sm:space-y-8" onSubmit={handleSignIn}>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-2 text-base sm:text-lg"
                htmlFor="username"
              >
                <FontAwesomeIcon icon={faUser} className="mr-2" />
                Username
              </label>
              <input
                className="w-full border-gray-300 border-2 rounded-md px-4 py-2 sm:px-5 sm:py-3 text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
                id="username"
                type="text"
                placeholder="Enter your username or email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </div>

            <div>
              <label
                className="block text-gray-700 font-semibold mb-2 text-base sm:text-lg"
                htmlFor="password"
              >
                <FontAwesomeIcon icon={faLock} className="mr-2" />
                Password
              </label>
              <input
                className="w-full border-gray-300 border-2 rounded-md px-4 py-2 sm:px-5 sm:py-3 text-base sm:text-lg focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            {successMessage && (
              <p className="text-green-600 text-base sm:text-lg">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                {successMessage}
              </p>
            )}

            {error && (
              <p className="text-red-600 text-base sm:text-lg">
                <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
                {error}
              </p>
            )}

            <button
              className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-full hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-gray-600 transform hover:scale-105 transition-transform duration-500 shadow-lg text-lg sm:text-xl"
              type="submit"
            >
              Sign In
            </button>

            <p className="text-gray-600 text-center text-base sm:text-lg">
              <button
                type="button"
                className="text-gray-700 hover:text-gray-800 underline transition-colors duration-300"
                onClick={handleForgotPassword}
              >
                Forgot Password?
              </button>
            </p>
          </form>

          <div className="flex justify-center mt-6">
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={handleGoogleLoginError}
              scope="openid profile email"
              className="w-full"
            />
          </div>
        </animated.div>

        {/* SSO: User Not Found (no auto-creation) */}
        {showNotFound && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 z-50">
            <div className="bg-white rounded-lg shadow-xl p-8 space-y-6 max-w-lg mx-auto text-center relative">
              <button
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                onClick={() => setShowNotFound(false)}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="text-2xl" />
              </button>
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className="text-red-500 text-4xl mb-4"
              />
              <h2 className="text-2xl font-semibold text-gray-800">
                User Not Found
              </h2>
              <p className="text-gray-700">
                We couldn’t find an ogPumper account for your Google sign-in.
                Per policy, we don’t create accounts automatically.
              </p>
              <p className="text-gray-700">
                We’ve notified <b>support@ogpumper.com</b>. You can contact{" "}
                <b>Support</b>, or link your Google to an existing account.
              </p>
              <div className="flex gap-3 justify-center mt-4">
                <a
                  href={`mailto:support@ogpumper.com?subject=Access%20Request%20(SSO%20User%20Not%20Found)&body=Hi%20Support,%0A%0AI%20attempted%20to%20sign%20in%20with%20Google%20SSO%20but%20don’t%20have%20an%20account.%20Could%20you%20assist%20me%20with%20access%3F%0A%0AThanks!`}
                  className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 transition duration-300"
                >
                  Email Support
                </a>
                <button
                  className="bg-gray-200 text-gray-800 py-2 px-6 rounded-md hover:bg-gray-300 transition duration-300"
                  onClick={() => setShowNotFound(false)}
                >
                  Back to Login
                </button>
                <button
                  className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 transition duration-300"
                  onClick={() => {
                    setShowNotFound(false);
                    setShowLinkModal(true);
                  }}
                >
                  I already have an account
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link existing account (UserID + Password) */}
        {showLinkModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 z-50">
            <div className="bg-white rounded-lg shadow-xl p-8 space-y-6 max-w-lg mx-auto text-center relative">
              <button
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkError("");
                }}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="text-2xl" />
              </button>
              <FontAwesomeIcon
                icon={faUser}
                className="text-blue-500 text-4xl mb-4"
              />
              <h2 className="text-2xl font-semibold text-gray-800">
                Link to Existing Account
              </h2>
              <p className="text-gray-700">
                Enter your ogPumper credentials to link with Google.
              </p>
              <input
                type="text"
                value={linkUserID}
                onChange={(e) => setLinkUserID(e.target.value)}
                className="w-full border-gray-300 border-2 rounded-md px-4 py-2 text-base focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
                placeholder="User ID"
              />
              <input
                type="password"
                value={linkPassword}
                onChange={(e) => setLinkPassword(e.target.value)}
                className="w-full border-gray-300 border-2 rounded-md px-4 py-2 text-base mt-3 focus:outline-none focus:ring-4 focus:ring-gray-400 focus:border-transparent transition-all duration-300"
                placeholder="Password"
              />
              {linkError && (
                <p className="text-red-600 text-base sm:text-lg">
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="mr-2"
                  />
                  {linkError}
                </p>
              )}
              <div className="flex gap-3 justify-center mt-2">
                <button
                  className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 transition duration-300"
                  onClick={handleLinkExisting}
                >
                  Link & Sign In
                </button>
                <button
                  className="bg-gray-200 text-gray-800 py-2 px-6 rounded-md hover:bg-gray-300 transition duration-300"
                  onClick={() => {
                    setShowLinkModal(false);
                    setLinkError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </GoogleOAuthProvider>
  );
}

export default SignInPage;
